// Scoring mapped directly to the prompt's requirements
export const calculateRisk = (answers) => {
  // Helper to safely parse scores
  const scoreMap = {
    q1: { 'Yes': 5, 'Partial': 3, 'No': 1 }, // MFA
    q2: { 'No': 5, 'Unknown': 2, 'Yes': 1 }, // Password sharing
    q3: { 'Yes': 5, 'Partial': 3, 'No': 1 }, // Endpoint
    q4: { 'Yes': 5, 'Unknown': 2, 'No': 1 }, // Firewall
    q5: { 'Yes': 5, 'Partial': 3, 'No': 1 }, // Backup
    q6: { 'Yes': 5, 'Never Tested': 1, 'No': 1 }, // Backup Test
    q7: { 'No': 5, 'Unsure': 3, 'Yes': 2 }, // Phishing exp
    q8: { 'Yes': 5, 'Unknown': 2, 'No': 1 }, // Email Filter
    q9: { 'Yes — Ongoing': 5, 'Yes — Annually': 4, 'No': 1 }, // Training
    q10: { 'Yes': 5, 'Informal/Undocumented': 3, 'No': 1 }, // Policy
    q11: { 'Yes, Confident': 5, 'Yes, Unsure': 3, 'No Insurance': 1 }, // Insurance
    q12: { 'No': 5, 'Yes': 4, 'Unsure': 1 } // Compliance (Unsure is high risk, Yes is moderate due to target on back, No is low regulatory risk)
  };

  const getScore = (q, val) => scoreMap[q][val] || 3; // Default to 3 if missing

  const domains = [
    {
      id: 'd1',
      name: 'Identity & Access',
      score: (getScore('q1', answers.q1) + getScore('q2', answers.q2)) / 2,
      finding: getFinding('d1', answers.q1, answers.q2)
    },
    {
      id: 'd2',
      name: 'Endpoint & Network',
      score: (getScore('q3', answers.q3) + getScore('q4', answers.q4)) / 2,
      finding: getFinding('d2', answers.q3, answers.q4)
    },
    {
      id: 'd3',
      name: 'Data Backup & Recovery',
      score: (getScore('q5', answers.q5) + getScore('q6', answers.q6)) / 2,
      finding: getFinding('d3', answers.q5, answers.q6)
    },
    {
      id: 'd4',
      name: 'Email & Phishing Risk',
      score: (getScore('q7', answers.q7) + getScore('q8', answers.q8)) / 2,
      finding: getFinding('d4', answers.q7, answers.q8)
    },
    {
      id: 'd5',
      name: 'Security Awareness & Policy',
      score: (getScore('q9', answers.q9) + getScore('q10', answers.q10)) / 2,
      finding: getFinding('d5', answers.q9, answers.q10)
    },
    {
      id: 'd6',
      name: 'Compliance & Insurance',
      score: (getScore('q11', answers.q11) + getScore('q12', answers.q12)) / 2,
      finding: getFinding('d6', answers.q11, answers.q12)
    }
  ];

  const overallScore = domains.reduce((acc, curr) => acc + curr.score, 0) / domains.length;

  return {
    domains,
    overallScore: overallScore.toFixed(1),
    summary: generateSummary(overallScore),
    recommendations: generateRecommendations(domains)
  };
};

const getFinding = (domain, a1, a2) => {
  // Logic to generate a 1-line finding based strictly on answers
  // a1 = first question in domain, a2 = second question in domain
  switch(domain) {
    case 'd1':
      if (a1 === 'No') return "Critical lack of MFA exposes accounts to immediate takeover.";
      if (a1 === 'Partial' || a2 === 'Yes') return "Inconsistent MFA and potential password sharing creating access vulnerabilities.";
      return "Strong identity controls with MFA enforced and good password hygiene.";
    case 'd2':
      if (a1 === 'No' || a2 === 'No') return "Missing fundamental endpoint or perimeter defenses.";
      if (a1 === 'Partial' || a2 === 'Unknown') return "Incomplete endpoint coverage or unverified firewall configurations.";
      return "Standard endpoint and network perimeter defenses are active.";
    case 'd3':
      if (a1 === 'No') return "No automated offsite backups, risking total data loss.";
      if (a2 === 'Never Tested' || a2 === 'No') return "Backups exist but lack verified testing, risking recovery failure.";
      return "Reliable, tested backup and recovery processes in place.";
    case 'd4':
      if (a2 === 'No') return "No email filtering in place, leaving users highly vulnerable to phishing.";
      if (a1 === 'Yes') return "Recent email incidents highlight need for stricter filtering and awareness.";
      return "Standard email security filtering is implemented.";
    case 'd5':
      if (a1 === 'No' || a2 === 'No') return "Lack of formal security policies and employee training.";
      if (a1 === 'Yes — Annually' && a2 === 'Informal/Undocumented') return "Basic training exists but lacks formal policy enforcement.";
      return "Active security awareness culture with documented policies.";
    case 'd6':
      if (a1 === 'No Insurance') return "Uninsured against cyber incidents with potential compliance blind spots.";
      if (a1 === 'Yes, Unsure') return "Carries insurance but may risk claim denial due to unverified controls.";
      return "Adequately insured with confidence in meeting carrier requirements.";
    default:
      return "Further assessment required.";
  }
};

export const getRiskLevel = (score) => {
  if (score < 2) return { label: 'CRITICAL', color: 'text-red-600', bg: 'bg-red-100' };
  if (score < 3) return { label: 'HIGH', color: 'text-orange-600', bg: 'bg-orange-100' };
  if (score < 4) return { label: 'MEDIUM', color: 'text-yellow-600', bg: 'bg-yellow-100' };
  if (score < 5) return { label: 'LOW', color: 'text-blue-600', bg: 'bg-blue-100' };
  return { label: 'MINIMAL', color: 'text-green-600', bg: 'bg-green-100' };
};

const generateSummary = (score) => {
  if (score < 2.5) {
    return "The organization is currently operating with critical security vulnerabilities that expose it to immediate risk of disruption or data breach. Foundational controls are missing or incomplete across multiple domains, requiring urgent remediation to establish a baseline defense.";
  } else if (score < 3.8) {
    return "The organization has some foundational security controls in place but exhibits significant gaps in enforcement, testing, or coverage. While not at critical risk, these inconsistencies leave the business vulnerable to common cyber threats and potential insurance claim denials.";
  } else {
    return "The organization demonstrates a proactive approach to cybersecurity with solid baseline controls implemented across most operational areas. Minor gaps exist, but the overall risk posture suggests a mature understanding of standard IT security requirements.";
  }
};

const generateRecommendations = (domains) => {
  // Sort domains by score (lowest first) and take top 3
  const sortedDomains = [...domains].sort((a, b) => a.score - b.score).slice(0, 3);
  
  const recMap = {
    'd1': {
      risk: "Incomplete Identity Verification",
      impact: "Unauthorized access to critical business systems and email accounts.",
      action: "Deploy and enforce Multi-Factor Authentication (MFA) across all remote access, email, and administrative accounts."
    },
    'd2': {
      risk: "Endpoint & Perimeter Vulnerabilities",
      impact: "Devices may be compromised by malware or ransomware without detection.",
      action: "Standardize next-generation endpoint protection (EDR) on all devices and audit edge firewall configurations."
    },
    'd3': {
      risk: "Unverified Data Resiliency",
      impact: "Extended downtime or permanent data loss during a ransomware event or hardware failure.",
      action: "Implement automated, air-gapped cloud backups and schedule bi-annual recovery testing."
    },
    'd4': {
      risk: "Email Borne Threats",
      impact: "Compromised credentials or financial fraud via targeted phishing attacks.",
      action: "Implement advanced email filtering and configure DMARC/DKIM/SPF records to prevent domain spoofing."
    },
    'd5': {
      risk: "Human Element Vulnerability",
      impact: "Employees may inadvertently click malicious links or bypass informal security rules.",
      action: "Establish a formal Acceptable Use Policy (AUP) and initiate ongoing, simulated phishing and awareness training."
    },
    'd6': {
      risk: "Financial & Regulatory Exposure",
      impact: "Potential fines or denied cyber insurance claims following a security incident.",
      action: "Conduct a formal gap assessment against cyber insurance requirements and applicable compliance frameworks."
    }
  };

  return sortedDomains.map(d => ({
    domain: d.name,
    ...recMap[d.id]
  }));
};